import type { AppleSignInResult, AuthError, AuthPort } from "./types";
import { sha256Hex } from "./sha256";

// Sign in with Apple JS 최소 타입(auth-session 3.8). 스크립트는 signInWithApple 첫 호출 때 지연 로드한다.
type AppleSignInResponse = {
  authorization: { code: string; id_token: string; state?: string };
};
type AppleAuth = {
  init(config: { clientId: string; scope?: string; redirectURI: string; state?: string; nonce?: string; usePopup?: boolean }): void;
  signIn(): Promise<AppleSignInResponse>;
};
declare global {
  interface Window {
    AppleID?: { auth: AppleAuth };
  }
}

// 웹 Google = 서버 리다이렉트(auth-session 3.9). start → Google 계정 선택 → 서버 callback → /login?googleHandle=… 로 복귀.
const GOOGLE_START_PATH = "/api/auth/google/start";

const APPLE_JS_SRC = "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/ko_KR/appleid.auth.js";
const appleServicesId = () => import.meta.env.VITE_APPLE_SERVICES_ID;
const appleRedirectUri = () => import.meta.env.VITE_APPLE_REDIRECT_URI;

let appleLoading: Promise<AppleAuth> | null = null;

function loadAppleJs(): Promise<AppleAuth> {
  if (window.AppleID?.auth) return Promise.resolve(window.AppleID.auth);
  appleLoading ??= new Promise<AppleAuth>((resolve, reject) => {
    const s = document.createElement("script");
    s.src = APPLE_JS_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => {
      const auth = window.AppleID?.auth;
      if (!auth) return reject({ code: "unavailable" } satisfies AuthError);
      resolve(auth);
    };
    s.onerror = () => {
      appleLoading = null;
      reject({ code: "unavailable" } satisfies AuthError);
    };
    document.head.appendChild(s);
  });
  return appleLoading;
}

const isApplePopupClosed = (e: unknown) =>
  typeof e === "object" && e !== null && "error" in e && /popup_closed_by_user|user_cancelled_authorize/.test(String((e as { error: unknown }).error));

/**
 * 웹 Google은 토큰을 직접 받지 않고 서버 리다이렉트(startGoogleRedirect)로 간다. 결과는 LoginPage가 ?googleHandle로 받는다.
 * Apple은 Sign in with Apple JS 팝업(usePopup). Apple에는 sha256(nonce)를 보내고 결과에는 원문을 실어 서버가 클레임 해시와 대조한다.
 * 팝업 응답의 authorization.code 는 authorizationCode 로 돌려준다(서버가 refresh 토큰으로 교환 → 탈퇴 때 revoke, AC0-269).
 */
export const webAuth: AuthPort = {
  async signInWithApple(): Promise<AppleSignInResult> {
    const clientId = appleServicesId();
    const redirectURI = appleRedirectUri();
    if (!clientId || !redirectURI) throw { code: "unavailable" } satisfies AuthError;
    const auth = await loadAppleJs();
    const state = crypto.randomUUID();
    const nonce = crypto.randomUUID();
    // 호출마다 init: state·nonce가 매번 새로워야 한다.
    auth.init({ clientId, scope: "email", redirectURI, state, nonce: await sha256Hex(nonce), usePopup: true });
    let res: AppleSignInResponse;
    try {
      res = await auth.signIn();
    } catch (e) {
      if (isApplePopupClosed(e)) throw { code: "cancelled", cause: e } satisfies AuthError;
      throw { code: "failed", cause: e } satisfies AuthError;
    }
    if (res.authorization?.state !== undefined && res.authorization.state !== state) throw { code: "failed", cause: "state_mismatch" } satisfies AuthError;
    const idToken = res.authorization?.id_token;
    if (!idToken) throw { code: "failed", cause: "missing_id_token" } satisfies AuthError;
    const code = res.authorization?.code;
    return { idToken, nonce, ...(code ? { authorizationCode: code } : {}) };
  },
  async signInWithGoogle(): Promise<{ idToken: string }> {
    throw { code: "unavailable" } satisfies AuthError; // 웹은 startGoogleRedirect
  },
  startGoogleRedirect(returnTo) {
    const q = returnTo ? `?${new URLSearchParams({ returnTo }).toString()}` : "";
    window.location.assign(`${import.meta.env.VITE_API_BASE ?? ""}${GOOGLE_START_PATH}${q}`);
  },
  async signOut() {},
};
